import {
  type Currency,
  LIVE_BREAK_BOX_INELIGIBLE_REASONS,
  LIVE_BREAK_DEFAULT_QUEUE_CAP,
  LIVE_BREAK_MAX_BOXES,
  LIVE_BREAK_MAX_MIN_BOX_PRICE_CENTS,
  LIVE_BREAK_MIN_BOXES,
  LIVE_BREAK_TITLE_MAX,
  type LiveBreakBoxIneligibleReason,
  type LiveBreakHouseProfile,
  parseAmount,
} from "@loot-os/api-contract";

import { amountInputValue } from "./currency";
import {
  isoToLocalDateTime,
  localDateTimeToIso,
  openerDelaySecondsText,
  parseBuyerQueueCap,
  parseOpenerDelaySeconds,
  parseQueueCap,
} from "./liveBreakStaff";
import { parseRatePercent, ratePercentText } from "./liveBreaks";

// A live break's settings as staff's two forms hold them: the edit form on a live break's page
// (`liveBreaks.update`, which takes only the keys that changed) and New house live break
// (`liveBreaks.createHouse`). Every number is the text as typed, checked before a save; the
// server checks again and names the rule it refuses on.

/** A box in the form's list, with what its row shows. */
export interface DraftBox {
  id: string;
  name: string;
  imageUrl: string;
  /** The box's own price. */
  priceCents: number;
}

export interface LiveBreakDraft {
  title: string;
  isPublic: boolean;
  /** A Twitch login; blank = none. */
  twitchChannel: string;
  isAcceptingPurchases: boolean;
  isAutoMode: boolean;
  isPaused: boolean;
  /** Viewers' stage delay, in seconds. */
  openerDelay: string;
  queueCap: string;
  /** Blank = no limit. */
  buyerQueueCap: string;
  isRestrictedToLiveBreakBoxes: boolean;
  /** The commission rate in percent; unused on a house live break. */
  rate: string;
  /** In the store's currency, as typed. */
  minBoxPrice: string;
  boxes: DraftBox[];
  /** A house live break's scheduled end, a `datetime-local` value; blank = none. */
  endsAt: string;
  /**
   * A house live break's dev profile: "idle_end" ends it once idle and respawns it (dev and
   * personal stages only; production refuses it). Chosen at creation alone; the edit never sends it.
   */
  houseProfile: LiveBreakHouseProfile | null;
}

/** The saved settings a draft starts from (the wire's `StaffLiveBreakWire` fields). */
export interface LiveBreakDraftSource {
  title: string;
  isPublic: boolean;
  twitchChannel: string | null;
  isAcceptingPurchases: boolean;
  isAutoMode: boolean;
  isPaused: boolean;
  openerDelayMs: number;
  queueCap: number;
  buyerQueueCap: number | null;
  isRestrictedToLiveBreakBoxes: boolean;
  commissionRateBps: number | null;
  minBoxPriceCents: number;
  endsAt: string | null;
  houseProfile: LiveBreakHouseProfile | null;
}

/** The form over a live break as saved, its boxes as the detail lists them. */
export function liveBreakDraftOf(
  liveBreak: LiveBreakDraftSource,
  boxes: readonly DraftBox[],
  currency: Pick<Currency, "multiplier">,
): LiveBreakDraft {
  return {
    title: liveBreak.title,
    isPublic: liveBreak.isPublic,
    twitchChannel: liveBreak.twitchChannel ?? "",
    isAcceptingPurchases: liveBreak.isAcceptingPurchases,
    isAutoMode: liveBreak.isAutoMode,
    isPaused: liveBreak.isPaused,
    openerDelay: openerDelaySecondsText(liveBreak.openerDelayMs),
    queueCap: String(liveBreak.queueCap),
    buyerQueueCap: liveBreak.buyerQueueCap === null ? "" : String(liveBreak.buyerQueueCap),
    isRestrictedToLiveBreakBoxes: liveBreak.isRestrictedToLiveBreakBoxes,
    rate: liveBreak.commissionRateBps === null ? "" : ratePercentText(liveBreak.commissionRateBps),
    minBoxPrice: amountInputValue(liveBreak.minBoxPriceCents, currency),
    boxes: [...boxes],
    endsAt: isoToLocalDateTime(liveBreak.endsAt),
    houseProfile: liveBreak.houseProfile,
  };
}

/**
 * The saved settings as one string, which changes only when a setting does: the edit form keys on
 * it, so another writer's change resets its draft while the stage's own writes (each moving
 * `updatedAt`) leave what staff are typing alone.
 */
export function liveBreakSettingsKey(
  liveBreak: LiveBreakDraftSource,
  boxIds: readonly string[],
): string {
  return JSON.stringify([
    liveBreak.title,
    liveBreak.isPublic,
    liveBreak.twitchChannel,
    liveBreak.isAcceptingPurchases,
    liveBreak.isAutoMode,
    liveBreak.isPaused,
    liveBreak.openerDelayMs,
    liveBreak.queueCap,
    liveBreak.buyerQueueCap,
    liveBreak.isRestrictedToLiveBreakBoxes,
    liveBreak.commissionRateBps,
    liveBreak.minBoxPriceCents,
    liveBreak.endsAt,
    boxIds,
  ]);
}

/** A new house live break's form: public, in Auto Mode, at the environment's minimum box price. */
export function newHouseLiveBreakDraft(
  defaultMinBoxPriceCents: number,
  currency: Pick<Currency, "multiplier">,
): LiveBreakDraft {
  return liveBreakDraftOf(
    {
      title: "",
      isPublic: true,
      twitchChannel: null,
      isAcceptingPurchases: true,
      isAutoMode: true,
      isPaused: false,
      openerDelayMs: 0,
      queueCap: LIVE_BREAK_DEFAULT_QUEUE_CAP,
      buyerQueueCap: null,
      isRestrictedToLiveBreakBoxes: false,
      commissionRateBps: null,
      minBoxPriceCents: defaultMinBoxPriceCents,
      endsAt: null,
      houseProfile: null,
    },
    [],
    currency,
  );
}

/** What the form needs to judge a draft. */
export interface LiveBreakDraftContext {
  currency: Pick<Currency, "symbol" | "multiplier">;
  isHouse: boolean;
  /** The environment's maximum rate, the rate field's ceiling. */
  maxRateBps: number;
  now: Date;
}

/** A field the draft cannot save, with the reason the form prints under it. */
export type LiveBreakDraftIssues = Partial<
  Record<
    | "title"
    | "openerDelay"
    | "queueCap"
    | "buyerQueueCap"
    | "rate"
    | "minBoxPrice"
    | "boxes"
    | "endsAt"
    | "isPaused",
    string
  >
>;

/**
 * What is wrong with a draft, by field; `{}` when it saves. Against `saved` (the edit form), a
 * rate above the maximum or an end already past is refused only when it changed: a live break
 * left above a lowered maximum, or Draining past its end, still saves its other settings.
 */
export function liveBreakDraftIssues(
  draft: LiveBreakDraft,
  context: LiveBreakDraftContext,
  saved: LiveBreakDraft | null = null,
): LiveBreakDraftIssues {
  const issues: LiveBreakDraftIssues = {};
  const title = draft.title.trim();
  if (title.length === 0) issues.title = "Give it a title.";
  else if (title.length > LIVE_BREAK_TITLE_MAX) {
    issues.title = `At most ${LIVE_BREAK_TITLE_MAX} characters.`;
  }
  if (parseOpenerDelaySeconds(draft.openerDelay) === null) {
    issues.openerDelay = "0 to 30 seconds, two decimals at most.";
  }
  if (parseQueueCap(draft.queueCap) === null) issues.queueCap = "A whole number from 5 to 2,000.";
  if (parseBuyerQueueCap(draft.buyerQueueCap) === undefined) {
    issues.buyerQueueCap = "Blank, or a whole number from 1 to 50.";
  }
  if (!context.isHouse) {
    const rate = parseRatePercent(draft.rate);
    if (rate === null) issues.rate = "A percent with two decimals at most.";
    else if (rate > context.maxRateBps && (!saved || rate !== parseRatePercent(saved.rate))) {
      issues.rate = `Can't be above the maximum rate, ${ratePercentText(context.maxRateBps)}%.`;
    }
  }
  const minBoxPriceCents = parseAmount(draft.minBoxPrice, context.currency);
  if (
    minBoxPriceCents === null ||
    minBoxPriceCents < 0 ||
    minBoxPriceCents > LIVE_BREAK_MAX_MIN_BOX_PRICE_CENTS
  ) {
    issues.minBoxPrice = "0 for no minimum, or a price.";
  }
  const boxCount = new Set(draft.boxes.map((box) => box.id)).size;
  if (boxCount < LIVE_BREAK_MIN_BOXES) issues.boxes = "Choose at least one box.";
  else if (boxCount > LIVE_BREAK_MAX_BOXES) issues.boxes = `At most ${LIVE_BREAK_MAX_BOXES} boxes.`;
  if (context.isHouse && draft.endsAt !== "") {
    const endsAt = localDateTimeToIso(draft.endsAt);
    if (endsAt === null) issues.endsAt = "Not a date.";
    else if (
      draft.endsAt !== saved?.endsAt &&
      new Date(endsAt).getTime() <= context.now.getTime()
    ) {
      issues.endsAt = "Choose a moment in the future.";
    }
  }
  if (draft.isPaused && !draft.isAutoMode) {
    issues.isPaused = "Only a live break in Auto Mode can be paused.";
  }
  return issues;
}

/** `liveBreaks.update`'s patch: the keys a draft changes, in the wire's units. */
export interface LiveBreakPatch {
  title?: string;
  twitchChannel?: string | null;
  isPublic?: boolean;
  isAcceptingPurchases?: boolean;
  isAutoMode?: boolean;
  isPaused?: boolean;
  openerDelayMs?: number;
  queueCap?: number;
  buyerQueueCap?: number | null;
  isRestrictedToLiveBreakBoxes?: boolean;
  commissionRateBps?: number;
  minBoxPriceCents?: number;
  boxIds?: string[];
  endsAt?: string | null;
}

function boxIdsOf(draft: LiveBreakDraft): string[] {
  return [...new Set(draft.boxes.map((box) => box.id))];
}

/**
 * The patch that saves `draft` over the live break `saved` was made from: only the keys whose
 * value changed, so a setting another writer moved meanwhile is left alone. Null while the draft
 * has an issue; `{}` when nothing changed.
 */
export function liveBreakDraftPatch(
  draft: LiveBreakDraft,
  saved: LiveBreakDraft,
  context: LiveBreakDraftContext,
): LiveBreakPatch | null {
  if (Object.keys(liveBreakDraftIssues(draft, context, saved)).length > 0) return null;
  const patch: LiveBreakPatch = {};
  const title = draft.title.trim();
  if (title !== saved.title.trim()) patch.title = title;
  const channel = draft.twitchChannel.trim() || null;
  if (channel !== (saved.twitchChannel.trim() || null)) patch.twitchChannel = channel;
  if (draft.isPublic !== saved.isPublic) patch.isPublic = draft.isPublic;
  if (draft.isAcceptingPurchases !== saved.isAcceptingPurchases) {
    patch.isAcceptingPurchases = draft.isAcceptingPurchases;
  }
  // A house live break is always in Auto Mode, so its form never sends the switch.
  if (!context.isHouse && draft.isAutoMode !== saved.isAutoMode) {
    patch.isAutoMode = draft.isAutoMode;
  }
  if (draft.isPaused !== saved.isPaused) patch.isPaused = draft.isPaused;
  const delay = parseOpenerDelaySeconds(draft.openerDelay);
  if (delay !== null && delay !== parseOpenerDelaySeconds(saved.openerDelay)) {
    patch.openerDelayMs = delay;
  }
  const queueCap = parseQueueCap(draft.queueCap);
  if (queueCap !== null && queueCap !== parseQueueCap(saved.queueCap)) patch.queueCap = queueCap;
  const buyerQueueCap = parseBuyerQueueCap(draft.buyerQueueCap);
  if (buyerQueueCap !== undefined && buyerQueueCap !== parseBuyerQueueCap(saved.buyerQueueCap)) {
    patch.buyerQueueCap = buyerQueueCap;
  }
  if (draft.isRestrictedToLiveBreakBoxes !== saved.isRestrictedToLiveBreakBoxes) {
    patch.isRestrictedToLiveBreakBoxes = draft.isRestrictedToLiveBreakBoxes;
  }
  if (!context.isHouse) {
    const rate = parseRatePercent(draft.rate);
    if (rate !== null && rate !== parseRatePercent(saved.rate)) patch.commissionRateBps = rate;
  }
  const minBoxPriceCents = parseAmount(draft.minBoxPrice, context.currency);
  if (
    minBoxPriceCents !== null &&
    minBoxPriceCents !== parseAmount(saved.minBoxPrice, context.currency)
  ) {
    patch.minBoxPriceCents = minBoxPriceCents;
  }
  const boxIds = boxIdsOf(draft);
  const savedIds = boxIdsOf(saved);
  if (boxIds.length !== savedIds.length || boxIds.some((id, i) => id !== savedIds[i])) {
    patch.boxIds = boxIds;
  }
  if (context.isHouse && draft.endsAt !== saved.endsAt) {
    patch.endsAt = draft.endsAt === "" ? null : localDateTimeToIso(draft.endsAt);
  }
  return patch;
}

/**
 * Which of a live break's settings staff may change now: every one, the end date alone (a house
 * live break Draining, where moving or clearing its end is the one edit the server takes, the
 * drain's undo), or none (no admin, closed, or a hosted live break Draining).
 */
export type LiveBreakEditScope = "all" | "endsAt" | "none";

/** The edit scope of `liveBreak` for a caller who may (`canEdit`) or may not edit it. */
export function liveBreakEditScope(
  liveBreak: { status: "open" | "closed"; isDraining: boolean; isHouse: boolean },
  canEdit: boolean,
): LiveBreakEditScope {
  if (!canEdit || liveBreak.status !== "open") return "none";
  if (liveBreak.isDraining) return liveBreak.isHouse ? "endsAt" : "none";
  return "all";
}

/**
 * The part of `patch` the scope lets through: all of it, its `endsAt` alone, or nothing, so a
 * Draining house live break's Save sends only the end and is offered only once the end moved.
 * Null (a draft with an issue) stays null.
 */
export function scopeLiveBreakPatch(
  patch: LiveBreakPatch | null,
  scope: LiveBreakEditScope,
): LiveBreakPatch | null {
  if (patch === null || scope === "all") return patch;
  if (scope === "none" || patch.endsAt === undefined) return {};
  return { endsAt: patch.endsAt };
}

/** `liveBreaks.createHouse`'s input from a draft; null while it has an issue. */
export function houseLiveBreakInput(
  draft: LiveBreakDraft,
  context: Pick<LiveBreakDraftContext, "currency" | "now">,
): {
  title: string;
  isPublic: boolean;
  boxIds: string[];
  endsAt: string | null;
  queueCap: number;
  isRestrictedToLiveBreakBoxes: boolean;
  buyerQueueCap: number | null;
  minBoxPriceCents: number;
  openerDelayMs: number;
  twitchChannel: string | null;
  /** Sent only when the draft has one. */
  houseProfile?: LiveBreakHouseProfile;
} | null {
  const issues = liveBreakDraftIssues(draft, { ...context, isHouse: true, maxRateBps: 0 });
  const queueCap = parseQueueCap(draft.queueCap);
  const buyerQueueCap = parseBuyerQueueCap(draft.buyerQueueCap);
  const minBoxPriceCents = parseAmount(draft.minBoxPrice, context.currency);
  const openerDelayMs = parseOpenerDelaySeconds(draft.openerDelay);
  if (
    Object.keys(issues).length > 0 ||
    queueCap === null ||
    buyerQueueCap === undefined ||
    minBoxPriceCents === null ||
    openerDelayMs === null
  ) {
    return null;
  }
  return {
    title: draft.title.trim(),
    isPublic: draft.isPublic,
    boxIds: boxIdsOf(draft),
    endsAt: draft.endsAt === "" ? null : localDateTimeToIso(draft.endsAt),
    queueCap,
    isRestrictedToLiveBreakBoxes: draft.isRestrictedToLiveBreakBoxes,
    buyerQueueCap,
    minBoxPriceCents,
    openerDelayMs,
    twitchChannel: draft.twitchChannel.trim() || null,
    ...(draft.houseProfile ? { houseProfile: draft.houseProfile } : {}),
  };
}

/** The boxes a save was refused for, each with its reason: box id → why it can't be sold. */
export type RefusedLiveBreakBoxes = ReadonlyMap<string, LiveBreakBoxIneligibleReason>;

function isIneligibleReason(value: unknown): value is LiveBreakBoxIneligibleReason {
  return (LIVE_BREAK_BOX_INELIGIBLE_REASONS as readonly unknown[]).includes(value);
}

/**
 * The boxes a `live_break_box_not_eligible` refusal named (`error.data.details`, the dashboard's
 * error shape): `details.boxes` gives each its own reason, and a bare `details.boxIds` takes
 * `details.reason` for every one. Empty for any other error, or a shape that drifted.
 */
export function refusedLiveBreakBoxesOf(error: unknown): RefusedLiveBreakBoxes {
  const refused = new Map<string, LiveBreakBoxIneligibleReason>();
  if (typeof error !== "object" || error === null) return refused;
  const data = (error as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return refused;
  const details = (data as { details?: unknown }).details;
  if (typeof details !== "object" || details === null) return refused;
  const { boxes, boxIds, reason } = details as Record<string, unknown>;
  if (Array.isArray(boxes)) {
    for (const box of boxes) {
      if (typeof box !== "object" || box === null) continue;
      const { boxId, reason: boxReason } = box as Record<string, unknown>;
      if (typeof boxId === "string" && isIneligibleReason(boxReason)) refused.set(boxId, boxReason);
    }
  }
  if (refused.size === 0 && Array.isArray(boxIds) && isIneligibleReason(reason)) {
    for (const id of boxIds) if (typeof id === "string") refused.set(id, reason);
  }
  return refused;
}
